import { Tabs as MuiTabs } from '@mui/material';
import React, { forwardRef } from 'react';
import { SxProps } from '../../utils';
import { TabProps } from './Tab';

export interface TabGroupProps<T extends string | number> {
  /**
   * A list of the Tabs contained within the TabGroup
   */
  children: React.ReactElement<TabProps<T>> | Array<React.ReactElement<TabProps<T>> | undefined>;
  /**
   * The value of the currently selected `Tab`. This determines which tab is active. It must match the `value` prop of one of the child `Tab` components.
   * If no `value` is provided on a `Tab`, its index is used as the default value.
   */
  value: T;
  /**
   * The label for the Tabs as a string.
   */
  'aria-label'?: string;
  /**
   * An id or list of ids separated by a space that label the Tabs.
   */
  'aria-labelledby'?: string;
  /**
   * Callback fired when the value changes.
   *
   * @param {React.SyntheticEvent} event The event source of the callback. **Warning**: This is a generic event not a change event.
   * @param {any} value We default to the index of the child (number)
   */
  onChange?: (event: React.SyntheticEvent, value: T) => void;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps;
  /**
   *  Determines additional display behavior of the tabs:
   *
   *  - `scrollable` will invoke scrolling properties and allow for horizontally
   *  scrolling (or swiping) of the tab bar.
   *  - `fullWidth` will make the tabs grow to use all the available space,
   *  which should be used for small views, like on mobile.
   *  - `standard` will render the default state.
   * @default 'standard'
   */
  variant?: 'standard' | 'scrollable' | 'fullWidth';
}

// TabGroupInner is defined as a separate generic function because
// forwardRef does not preserve generic parameters in JSX usage.
// This lets us preserve type safety for value/onChange while still supporting forwardRef.
function TabGroupInner<T extends string | number>(props: TabGroupProps<T>, ref: React.Ref<HTMLButtonElement>) {
  const { children, onChange, ...rest } = props;

  const handleChange = (event: React.SyntheticEvent, newValue: unknown) => {
    onChange?.(event, newValue as T);
  };

  return (
    <MuiTabs {...rest} ref={ref} onChange={handleChange}>
      {children}
    </MuiTabs>
  );
}

export const TabGroup = forwardRef(TabGroupInner) as <T extends string | number>(
  props: TabGroupProps<T> & { ref?: React.Ref<HTMLButtonElement> }
) => ReturnType<typeof TabGroupInner>;
